import type { LiftLook } from '../view/liftParts.js';
import { liftLookOf } from '../view/liftParts.js';
import { el } from './dom.js';

/**
 * Into a shift by lift, and back out by it (ADR-0057).
 *
 * Play on the building zooms into the shift's floor, where the lift waits with
 * its doors open; the lift's doors close over the screen, the indicator above
 * them counts the floors up from the street, the chime sounds, and they open
 * on the shift. Leaving runs it the other way: down to the street, and the
 * building zooms back out from the floor. A retry only closes and opens.
 *
 * Presentation only: the shift's clock is held until the doors are open, and
 * nothing here decides anything about a run. The doors are each building's own
 * finish, as on the home screen's lift (ADR-0027): never bought.
 */

/** A floor on the home screen to zoom into, or out from. */
export interface Zoom {
  /** What scales: the home screen's stage. */
  readonly stage: HTMLElement;
  /** Where to: the lift parked at the floor, or the floor itself. */
  readonly target: HTMLElement;
}

export interface Ride {
  /** Whose lift: its lintel's colours and its doors' finish. */
  readonly building: string;
  /** What the indicator shows, in order; the last is where the doors open. */
  readonly stops: readonly string[];
  readonly going: 'up' | 'down';
  /** On the way in: zoom into this before the doors close. */
  readonly into?: Zoom | null;
  /** On the way out: what to zoom out from, asked once the building is behind the doors. */
  readonly outOf?: () => Zoom | null;
  /** The arrival chime, as the doors open. */
  readonly chime: () => void;
  /** The doors' own sound, as they start to close or to open. */
  readonly slide: (way: 'close' | 'open') => void;
}

const ZOOM_MS = 520;
const CLOSE_MS = 380;
const STEP_MS = 160;
const OPEN_MS = 420;
/** A short fade instead, for a player who asked for less motion. */
const STILL_MS = 160;

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

function reducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** The indicator's stops: from the street up to the shift, three floors at most. */
export function stopsUp(floor: number): string[] {
  const from = Math.max(1, floor - 2);
  const floors = Array.from({ length: floor - from + 1 }, (_, i) => pad(from + i));
  return ['G', ...floors];
}

/** From the shift down to the street. */
export function stopsDown(floor: number): string[] {
  return [...stopsUp(floor)].reverse();
}

const pad = (n: number): string => String(n).padStart(2, '0');

/** Scale the stage so the target comes to its middle, filling a good part of it. */
function aim(zoom: Zoom): void {
  const stage = zoom.stage.getBoundingClientRect();
  const box = zoom.target.getBoundingClientRect();
  if (stage.width === 0 || box.height === 0) return;
  const ox = box.left + box.width / 2 - stage.left;
  const oy = box.top + box.height / 2 - stage.top;
  const scale = Math.min(3.2, Math.max(1.8, (stage.height * 0.5) / box.height));
  const s = zoom.stage.style;
  s.setProperty('--zoom-ox', `${ox}px`);
  s.setProperty('--zoom-oy', `${oy}px`);
  s.setProperty('--zoom-dx', `${stage.width / 2 - ox}px`);
  s.setProperty('--zoom-dy', `${stage.height / 2 - oy}px`);
  s.setProperty('--zoom-k', String(scale));
}

function unzoom(stage: HTMLElement): void {
  delete stage.dataset.zoom;
  for (const name of ['--zoom-ox', '--zoom-oy', '--zoom-dx', '--zoom-dy', '--zoom-k']) {
    stage.style.removeProperty(name);
  }
}

export class LiftDoors {
  readonly root: HTMLElement;
  private readonly arrow: HTMLElement;
  private readonly number: HTMLElement;
  private riding = false;

  constructor() {
    this.arrow = el('span', { class: 'doors-arrow', 'aria-hidden': 'true' });
    this.number = el('span', { class: 'doors-number' });
    this.root = el('div', { class: 'lift-doors', 'data-state': 'open', 'aria-hidden': 'true' }, [
      el('div', { class: 'door door-left' }),
      el('div', { class: 'door door-right' }),
      el('div', { class: 'doors-lintel' }, [
        el('div', { class: 'doors-window' }, [this.arrow, this.number]),
      ]),
    ]);
  }

  /** A ride is under way: a second Play waits for it rather than starting another. */
  get busy(): boolean {
    return this.riding;
  }

  /**
   * Close the doors (zooming in first, if asked), run `behind` while they are
   * shut, then open them. Resolves once they are open; `behind` failing still
   * opens them, so a ride can never leave the screen covered.
   */
  async ride(ride: Ride, behind: () => Promise<void> | void): Promise<void> {
    this.riding = true;
    const still = reducedMotion();
    const look: LiftLook = liftLookOf(ride.building);
    this.root.dataset.building = ride.building;
    this.root.dataset.look = look;
    this.root.dataset.going = ride.going;
    this.root.dataset.still = String(still);
    this.number.textContent = ride.stops[0] ?? '';
    try {
      await this.shut(ride, still);
      const work = Promise.resolve().then(behind);
      await this.count(ride.stops, still);
      await work.catch(() => undefined);
      if (ride.into) unzoom(ride.into.stage);
      await this.open(ride, still);
    } finally {
      this.root.dataset.state = 'open';
      this.root.dataset.arrived = 'false';
      this.riding = false;
    }
  }

  private async shut(ride: Ride, still: boolean): Promise<void> {
    const into = ride.into ?? null;
    if (into && !still) {
      aim(into);
      into.stage.dataset.zoom = 'in';
      // The doors start closing as the zoom gets there.
      await wait(ZOOM_MS * 0.55);
    }
    this.root.dataset.arrived = 'false';
    this.root.dataset.state = 'closed';
    ride.slide('close');
    await wait(still ? STILL_MS : CLOSE_MS);
  }

  private async count(stops: readonly string[], still: boolean): Promise<void> {
    for (const [i, stop] of stops.entries()) {
      this.number.textContent = stop;
      if (i < stops.length - 1) await wait(still ? 0 : STEP_MS);
    }
    this.root.dataset.arrived = 'true';
    await wait(still ? 0 : 140);
  }

  private async open(ride: Ride, still: boolean): Promise<void> {
    const out = still ? null : (ride.outOf?.() ?? null);
    if (out) {
      // Start from the floor, filling the screen, and let it fall back into place.
      aim(out);
      out.stage.dataset.zoom = 'held';
      out.stage.getBoundingClientRect();
      out.stage.dataset.zoom = 'back';
    }
    ride.chime();
    ride.slide('open');
    this.root.dataset.state = 'open';
    await wait(still ? STILL_MS : OPEN_MS);
    if (out) {
      await wait(Math.max(0, ZOOM_MS - OPEN_MS));
      unzoom(out.stage);
    }
  }
}
